import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight, Check, Loader2, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import QuestionInput from "@/components/QuestionInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Onboarding() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/onboarding`)
      .then(({ data }) => {
        setQuestions(data.preguntas || []);
        setAnswers(data.respuestas || {});
      })
      .catch(() => toast.error("No pudimos cargar el onboarding"))
      .finally(() => setLoading(false));
  }, []);

  const q = questions[idx];
  const total = questions.length;
  const progress = total ? ((idx + 1) / total) * 100 : 0;

  const save = async () => {
    if (q.required) {
      const v = answers[q.id];
      const empty = v === undefined || v === "" || (Array.isArray(v) && v.length === 0);
      if (empty) {
        toast.error("Esta pregunta es obligatoria");
        return false;
      }
    }
    setSaving(true);
    try {
      await axios.put(`${API}/onboarding`, { respuestas: { [q.id]: answers[q.id] ?? "" } });
      return true;
    } catch {
      toast.error("No pudimos guardar tu respuesta");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    const ok = await save();
    if (!ok) return;
    if (idx < total - 1) {
      setIdx(idx + 1);
    } else {
      // Final
      await refresh();
      toast.success("Onboarding completo. Estamos generando tus 3 documentos esenciales en tu cuenta.", { duration: 6000 });
      navigate("/cuenta");
    }
  };

  if (loading)
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="w-7 h-7 text-[#1E3A8A] animate-spin" />
      </div>
    );

  if (!q) return null;

  return (
    <>
      <SEO title="Onboarding" description="Configura los datos de tu empresa una sola vez." path="/onboarding" noindex />
      <section className="px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between text-xs font-semibold tracking-wider uppercase text-slate-500 mb-3">
            <Link to="/cuenta" className="hover:text-slate-700">← Mi cuenta</Link>
            <span data-testid="onboarding-progress">Paso {idx + 1} de {total}</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-10">
            <motion.div
              className="h-full bg-[#1E3A8A]"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <h1 data-testid="onboarding-question" className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
                {q.label}
                {q.required && <span className="text-rose-600 ml-1">*</span>}
              </h1>
              {q.help && <p className="mt-3 text-sm text-slate-500 leading-relaxed">{q.help}</p>}

              <div className="mt-8">
                <QuestionInput
                  q={q}
                  value={answers[q.id]}
                  onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
                />
              </div>

              {idx === total - 1 && (
                <div
                  data-testid="onboarding-final-notice"
                  className="mt-8 rounded-2xl border border-blue-200 bg-blue-50/60 p-5 flex items-start gap-3"
                >
                  <div className="w-8 h-8 rounded-xl bg-blue-700 text-white grid place-items-center shrink-0">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-blue-700 mb-1.5">
                      Al terminar
                    </p>
                    <p className="text-sm text-slate-800 leading-relaxed">
                      Generamos automáticamente tus <strong>3 documentos esenciales</strong> con tus datos: Política de Privacidad, Cláusula de Consentimiento y Catálogo de Datos Personales (RAT). Verás el progreso en tu cuenta en unos minutos.
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 flex items-center justify-between gap-4">
            <button
              onClick={() => idx > 0 && setIdx(idx - 1)}
              disabled={idx === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Atrás
            </button>
            <button
              data-testid="onboarding-next"
              onClick={next}
              disabled={saving}
              className="inline-flex items-center gap-2 h-12 px-7 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] active:scale-[0.98] disabled:opacity-60 text-white font-semibold transition-all"
            >
              {idx === total - 1 ? (<>Terminar onboarding <Check className="w-4 h-4" /></>) : (<>Siguiente <ChevronRight className="w-4 h-4" /></>)}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
